// ─────────────────────────────────────────────────────────────
// MUI theme — VS Code / JupyterLab aesthetic
//
// Design targets:
// - Electron microscopy scientists: technical, dense, professional
// - Dark-first (matches VS Code defaults)
// - Accent: blue-teal range, not colorful consumer palette
// - Typography: tight, information-dense
// - Minimal decorative chrome
// ─────────────────────────────────────────────────────────────

import { createTheme } from "@mui/material/styles";
import type { PaletteMode } from "@mui/material";

// ─────────────────────────────────────────────────────────────
// MD3 surface container tokens — used for depth/hierarchy
// without elevation shadows. Five-tier stack:
//
//   surfaceContainerLowest  ← most lifted in light mode (white)
//   surfaceContainerLow
//   surfaceContainer
//   surfaceContainerHigh
//   surfaceContainerHighest ← most lifted in dark mode
//
// The page bg (`background.default`) sits between Low and
// Container — so cells using Container/High visually lift
// off the page in both modes.
// ─────────────────────────────────────────────────────────────

declare module "@mui/material/styles" {
  interface Palette {
    surfaceContainerLowest: string;
    surfaceContainerLow: string;
    surfaceContainer: string;
    surfaceContainerHigh: string;
    surfaceContainerHighest: string;
  }
  interface PaletteOptions {
    surfaceContainerLowest?: string;
    surfaceContainerLow?: string;
    surfaceContainer?: string;
    surfaceContainerHigh?: string;
    surfaceContainerHighest?: string;
  }
}

// Light-mode neutral tonal stack — pure greys (no purple tint),
// matches the VS Code aesthetic of the rest of the theme.
const LIGHT_SURFACE_CONTAINER_LOWEST  = "#ffffff";
const LIGHT_SURFACE_CONTAINER_LOW     = "#fafafa";
const LIGHT_SURFACE_CONTAINER         = "#f0f0f0";
const LIGHT_SURFACE_CONTAINER_HIGH    = "#e8e8e8";
const LIGHT_SURFACE_CONTAINER_HIGHEST = "#e0e0e0";

// Dark-mode neutral tonal stack — re-tuned 2026-05-27 for a
// legible MD3 dark elevation ladder. The old stack stepped only
// ~8 hex values between page/card/dialog (#1e1e1e → #252526 →
// #2d2d30) so surfaces read as one flat muddy plane. MD3 dark
// surfaces get LIGHTER as elevation rises; the steps below are
// widened to ~6–7 L* per tier so each tier reads as a distinct
// plane. The page bg (#1c1c1c) sits just above Lowest/Low, so any
// cell on Container/High/Highest visibly lifts off the page.
//
//   Lowest   #141414  — recessed wells (code gutters, inset panels)
//   Low      #1f1f1f  — page-adjacent fills
//   Container#272727  — cards / standard panels (lift off the page)
//   High     #2f2f2f  — dialogs / menus / popovers
//   Highest  #383838  — top-most lifted chrome (app bar, FAB-likes)
const DARK_SURFACE_CONTAINER_LOWEST   = "#141414";
const DARK_SURFACE_CONTAINER_LOW      = "#1f1f1f";
const DARK_SURFACE_CONTAINER          = "#272727";
const DARK_SURFACE_CONTAINER_HIGH     = "#2f2f2f";
const DARK_SURFACE_CONTAINER_HIGHEST  = "#383838";

// VS Code-like color constants, aligned to the ladder above so the
// three workhorse surfaces are each a clear tier apart:
//   page    = #1c1c1c (between Lowest and Low)
//   paper   = surfaceContainer (#272727) — base card/panel plane
//   elevated= surfaceContainerHigh (#2f2f2f) — dialogs/menus
const DARK_BG_DEFAULT = "#1c1c1c";
const DARK_BG_PAPER = "#272727";
const DARK_BG_ELEVATED = "#2f2f2f";
// Text — brightened for WCAG AA. Old primary #d4d4d4 was fine, but
// secondary #9d9d9d only hit ~4.0:1 on the lifted #2d2d30 surfaces.
// #b0b0b0 clears AA (4.5:1) for body text on every tier up to High.
const DARK_TEXT_PRIMARY = "#e3e3e3";
const DARK_TEXT_SECONDARY = "#b0b0b0";
// Divider/border lightened so panels separate when the elevation
// step alone is subtle (MD3 leans on hairline outlines in dark mode).
const DARK_DIVIDER = "#3a3a3a";
const DARK_BORDER = "#4a4a4a";

const LIGHT_BG_DEFAULT = "#f5f5f5";
const LIGHT_BG_PAPER = "#ffffff";
const LIGHT_TEXT_PRIMARY = "#1e1e1e";
const LIGHT_TEXT_SECONDARY = "#6b6b6b";
const LIGHT_DIVIDER = "#e0e0e0";

// Accent palette — VS Code blue with subtle teal shift
const PRIMARY_MAIN_LIGHT = "#0288d1"; // Light Blue 700 — confident on white surfaces
const PRIMARY_MAIN_DARK = "#4fc3f7"; // VS Code light-blue accent — reads on dark surfaces
const PRIMARY_DARK = "#0288d1";
const PRIMARY_LIGHT = "#81d4fa";
const SECONDARY_MAIN = "#80cbc4"; // Teal for secondary
const ERROR_MAIN = "#f48771"; // VS Code error red-orange
const WARNING_MAIN = "#dcdcaa"; // VS Code warning yellow
const SUCCESS_MAIN = "#4ec994"; // VS Code success green

// Effort → color for badges
export const EFFORT_COLORS: Record<string, string> = {
  XS: "#4caf50",
  S:  "#8bc34a",
  M:  "#ff9800",
  L:  "#f44336",
  XL: "#9c27b0",
};

// Status → color for badges
export const STATUS_COLORS: Record<string, string> = {
  "Idea":      "#607d8b",
  "Ready":     "#2196f3",
  "Candidate": "#ff9800",
  "In Sprint": "#4fc3f7",
  "Done":      "#4ec994",
};

// Column header accent colors — 3 columns only
export const COLUMN_COLORS: Record<string, string> = {
  "Candidate Pool": "#607d8b",
  "Committed":      "#ff9800",
  "Done":           "#4ec994",
};

// Priority → color for badges
export const PRIORITY_COLORS: Record<string, string> = {
  "Low":    "#607d8b",
  "Medium": "#1976d2",
  "High":   "#e65100",
  "Urgent": "#c62828",
};

export function createDashboardTheme(mode: PaletteMode) {
  const isDark = mode === "dark";

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? PRIMARY_MAIN_DARK : PRIMARY_MAIN_LIGHT,
        dark: PRIMARY_DARK,
        light: PRIMARY_LIGHT,
        contrastText: isDark ? "#000000" : "#ffffff",
      },
      secondary: {
        main: SECONDARY_MAIN,
        contrastText: isDark ? "#000000" : "#ffffff",
      },
      error: {
        main: ERROR_MAIN,
      },
      warning: {
        main: WARNING_MAIN,
      },
      success: {
        main: SUCCESS_MAIN,
      },
      background: {
        default: isDark ? DARK_BG_DEFAULT : LIGHT_BG_DEFAULT,
        paper: isDark ? DARK_BG_PAPER : LIGHT_BG_PAPER,
      },
      text: {
        primary: isDark ? DARK_TEXT_PRIMARY : LIGHT_TEXT_PRIMARY,
        secondary: isDark ? DARK_TEXT_SECONDARY : LIGHT_TEXT_SECONDARY,
      },
      divider: isDark ? DARK_DIVIDER : LIGHT_DIVIDER,
      // MD3 surface container stack — accessed in sx as
      // bgcolor: "surfaceContainer" etc.
      surfaceContainerLowest:  isDark ? DARK_SURFACE_CONTAINER_LOWEST  : LIGHT_SURFACE_CONTAINER_LOWEST,
      surfaceContainerLow:     isDark ? DARK_SURFACE_CONTAINER_LOW     : LIGHT_SURFACE_CONTAINER_LOW,
      surfaceContainer:        isDark ? DARK_SURFACE_CONTAINER         : LIGHT_SURFACE_CONTAINER,
      surfaceContainerHigh:    isDark ? DARK_SURFACE_CONTAINER_HIGH    : LIGHT_SURFACE_CONTAINER_HIGH,
      surfaceContainerHighest: isDark ? DARK_SURFACE_CONTAINER_HIGHEST : LIGHT_SURFACE_CONTAINER_HIGHEST,
      // Custom surface colors accessible via theme.palette
      ...(isDark && {
        action: {
          hover: "rgba(255,255,255,0.06)",
          selected: "rgba(79,195,247,0.12)",
          focus: "rgba(255,255,255,0.08)",
          disabled: "rgba(255,255,255,0.26)",
          disabledBackground: "rgba(255,255,255,0.06)",
        },
      }),
    },

    shape: {
      borderRadius: 4, // Tight, VS Code-like — not rounded consumer UI
    },

    typography: {
      fontFamily: '"Roboto", "Helvetica Neue", "Arial", sans-serif',
      fontSize: 13, // Dense default
      htmlFontSize: 16,

      // Page/section titles
      h1: {
        fontSize: "1.5rem",
        fontWeight: 400,
        lineHeight: 1.4,
        letterSpacing: "0em",
      },
      h2: {
        fontSize: "1.25rem",
        fontWeight: 500,
        lineHeight: 1.4,
        letterSpacing: "0em",
      },
      h3: {
        fontSize: "1rem",
        fontWeight: 500,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: "0.9375rem",
        fontWeight: 500,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: "0.8125rem",
        fontWeight: 500,
        lineHeight: 1.4,
      },

      // Body
      body1: {
        fontSize: "0.875rem",
        lineHeight: 1.5,
      },
      body2: {
        fontSize: "0.8125rem",
        lineHeight: 1.5,
      },

      // Labels
      caption: {
        fontSize: "0.75rem",
        lineHeight: 1.4,
        letterSpacing: "0.02em",
      },
      overline: {
        fontSize: "0.6875rem",
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      },
      subtitle1: {
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: 1.4,
      },
      subtitle2: {
        fontSize: "0.8125rem",
        fontWeight: 500,
        lineHeight: 1.4,
      },
      button: {
        fontSize: "0.8125rem",
        fontWeight: 500,
        letterSpacing: "0.02em",
        textTransform: "none", // VS Code buttons are sentence case
      },
    },

    // MD3-compatible transitions
    transitions: {
      duration: {
        shortest: 100,
        shorter: 150,
        short: 200,
        standard: 250,
        complex: 350,
      },
      easing: {
        easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
        easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
        easeIn: "cubic-bezier(0.4, 0, 1, 1)",
        sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
      },
    },

    components: {
      // ── App-level chrome ──
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? DARK_BG_DEFAULT : LIGHT_BG_DEFAULT,
            scrollbarColor: isDark
              ? `${DARK_BORDER} ${DARK_BG_PAPER}`
              : "#c0c0c0 #f0f0f0",
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: isDark ? DARK_BG_PAPER : "#f0f0f0",
            },
            "&::-webkit-scrollbar-thumb": {
              background: isDark ? DARK_BORDER : "#c0c0c0",
              borderRadius: "4px",
            },
          },
        },
      },

      // ── Drawer — sidebar nav ──
      MuiDrawer: {
        styleOverrides: {
          paper: {
            // Drawer sits at the Low tier — a hair above the page so
            // the nav rail reads as its own plane without competing
            // with cards (which sit on Container/High).
            backgroundColor: isDark ? DARK_SURFACE_CONTAINER_LOW : "#ffffff",
            borderRight: `1px solid ${isDark ? DARK_DIVIDER : LIGHT_DIVIDER}`,
            backgroundImage: "none",
          },
        },
      },

      // ── AppBar ──
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            // App bar is top-most chrome → Highest tier.
            backgroundColor: isDark ? DARK_SURFACE_CONTAINER_HIGHEST : "#f3f3f3",
            borderBottom: `1px solid ${isDark ? DARK_DIVIDER : LIGHT_DIVIDER}`,
            color: isDark ? DARK_TEXT_PRIMARY : LIGHT_TEXT_PRIMARY,
            backgroundImage: "none",
          },
        },
      },

      // ── Cards — flat, bordered, VS Code panel style ──
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            border: `1px solid ${isDark ? DARK_BORDER : LIGHT_DIVIDER}`,
            borderRadius: "4px",
            backgroundImage: "none",
            backgroundColor: isDark ? DARK_BG_ELEVATED : LIGHT_BG_PAPER,
          },
        },
      },

      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: "12px 14px",
            "&:last-child": { paddingBottom: "12px" },
          },
        },
      },

      // ── Paper ──
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: `1px solid ${isDark ? DARK_BORDER : LIGHT_DIVIDER}`,
          },
          elevation0: {
            border: `1px solid ${isDark ? DARK_BORDER : LIGHT_DIVIDER}`,
          },
        },
      },

      // ── Buttons ──
      MuiButton: {
        defaultProps: {
          disableElevation: true,
          size: "small",
        },
        styleOverrides: {
          root: {
            borderRadius: "4px",
            padding: "4px 12px",
            minHeight: "28px",
          },
          contained: {
            backgroundColor: isDark ? "#0e639c" : "#007acc",
            color: "#ffffff",
            "&:hover": {
              backgroundColor: isDark ? "#1177bb" : "#006ab5",
            },
          },
          outlined: {
            borderColor: isDark ? DARK_BORDER : "#c5c5c5",
            color: isDark ? DARK_TEXT_PRIMARY : LIGHT_TEXT_PRIMARY,
            "&:hover": {
              backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
              borderColor: isDark ? "#888" : "#aaa",
            },
          },
        },
      },

      MuiIconButton: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          root: {
            borderRadius: "4px",
            padding: "4px",
          },
        },
      },

      // ── Chips ──
      MuiChip: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          root: {
            borderRadius: "3px",
            height: "20px",
            fontSize: "0.6875rem",
            fontWeight: 500,
          },
          label: {
            paddingLeft: "6px",
            paddingRight: "6px",
          },
        },
      },

      // ── Text inputs ──
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: "4px",
            fontSize: "0.875rem",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? DARK_BORDER : "#c5c5c5",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? "#888" : "#aaa",
            },
          },
          input: {
            padding: "6px 10px",
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: "0.8125rem",
          },
        },
      },

      MuiTextField: {
        defaultProps: {
          variant: "outlined",
          size: "small",
        },
      },

      MuiSelect: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          select: {
            fontSize: "0.875rem",
            padding: "6px 10px",
          },
        },
      },

      // ── Lists ──
      MuiListItem: {
        styleOverrides: {
          root: {
            padding: "2px 8px",
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: "4px",
            padding: "5px 10px",
            margin: "1px 4px",
            "&.Mui-selected": {
              backgroundColor: isDark
                ? "rgba(79,195,247,0.15)"
                : "rgba(0,122,204,0.12)",
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(79,195,247,0.20)"
                  : "rgba(0,122,204,0.18)",
              },
            },
          },
        },
      },

      // ── Divider ──
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isDark ? DARK_DIVIDER : LIGHT_DIVIDER,
          },
        },
      },

      // ── Tooltip ──
      MuiTooltip: {
        defaultProps: {
          placement: "top",
          arrow: true,
        },
        styleOverrides: {
          tooltip: {
            fontSize: "0.75rem",
            // Floating transient → sits above the Highest tier so it
            // reads clearly over app bar / dialogs.
            backgroundColor: isDark ? "#4a4a4a" : "#333",
            color: isDark ? "#f5f5f5" : "#fff",
            padding: "4px 8px",
          },
        },
      },

      // ── Dialog ──
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? DARK_BG_ELEVATED : LIGHT_BG_PAPER,
            borderRadius: "6px",
          },
        },
      },

      // ── Tabs ──
      MuiTab: {
        styleOverrides: {
          root: {
            fontSize: "0.8125rem",
            fontWeight: 500,
            textTransform: "none",
            minHeight: "36px",
            padding: "6px 14px",
          },
        },
      },

      // ── Badge ──
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontSize: "0.6875rem",
            height: "16px",
            minWidth: "16px",
            padding: "0 4px",
          },
        },
      },
    },
  });
}

// Export a default dark theme for initial render (avoids flicker)
export const defaultTheme = createDashboardTheme("dark");
